import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useOpenInNewTab } from '@/hooks/useOpenInNewTab';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FolderOpen, Calendar, AlertCircle, FileText, Crown, Sparkles, Download, CheckCircle, Loader2, Trash2 } from 'lucide-react';
import PageLoader from "@/components/PageLoader";
import PageTransition from "@/components/PageTransition";
import { useReportsByPeriods, useReportPeriods } from '@/hooks/useSupabaseReports';
import { useUsersFromSupabase } from '@/hooks/useSupabaseUsers';
import MechanicalDashboardTab from '@/components/MechanicalDashboardTab';
import HRDashboard from '@/pages/HRDashboard';
import { useOfflineReport, useNetworkStatus, useOfflineReports } from '@/hooks/useOfflineReport';
import { openOfflineReport } from '@/services/offlineReportService';
import { useToast } from '@/hooks/use-toast';
import {
  getReportFileUrl,
  hasReportFile,
  type ReportFileRefs,
} from '@/integrations/supabase/reportFileService';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const WEEKLY_GROUP_ID = 'C601DF99-C5492DF9';
const QUALITY_GROUP_ID = 'F50833CA-660C8418';
const BCDGNV_GROUP_ID = 'BCDGNV-EVAL2025';

/**
 * Trang này dùng shape ReportItem viết hoa (FullURL, ReportName…), còn
 * reportFileService làm việc với tên cột thật của bảng reports. Đổi qua lại ở
 * một chỗ duy nhất để không phải rải `as any` khắp nơi.
 */
const toFileRefs = (report: unknown): ReportFileRefs => {
  const r = report as Record<string, unknown>;
  return {
    id: ((r.id as string | null) ?? (r.ID as string | null)) ?? null,
    file_id: (r.file_id as string | null) ?? null,
    supabase_file_path: (r.supabase_file_path as string | null) ?? null,
    full_url: ((r.full_url as string | null) ?? (r.FullURL as string | null)) ?? null,
    ai_storage_path: (r.ai_storage_path as string | null) ?? null,
    ai_version_url: (r.ai_version_url as string | null) ?? null,
    report_name: ((r.report_name as string | null) ?? (r.ReportName as string | null)) ?? null,
  };
};

// ── Offline save button ───────────────────────────────────────────────────────

const OfflineSaveButton = ({ report }: { report: ReportItem }) => {
  const { toast } = useToast();
  const { isOffline, isSaving, isChecking, saveProgress, saveOffline, checkFreshness } = useOfflineReport(report.ID || (report as any).id || '');
  const { isOnline } = useNetworkStatus();
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);

  const buildPayload = () => ({
    id: report.ID || (report as any).id || '',
    report_name: report.ReportName,
    period: (report as any).Period,
    created_on: report.CreatedOn,
    full_url: report.FullURL,
    file_url: (report as any).file_url,
    file_id: (report as any).file_id,
    supabase_file_path: (report as any).supabase_file_path,
    summary_by_ai: (report as any).summary_by_ai,
    is_summary: report.is_summary,
    id_group: (report as any).IDGroup,
    created_by_user_id: report.CreatedByUserID,
    auth_user_id: (report as any).auth_user_id,
    updated_at: (report as any).updated_at,
  });

  const handleSave = async () => {
    const result = await saveOffline(buildPayload());
    if (result.success) {
      toast({ title: 'Đã lưu offline', description: `"${report.ReportName}" sẵn sàng đọc khi mất mạng.` });
    } else {
      toast({ title: 'Không thể lưu offline', description: result.message, variant: 'destructive' });
    }
  };

  const handleCheckForUpdate = async () => {
    if (!isOnline) {
      toast({ title: 'Không có mạng', description: 'Không thể kiểm tra bản mới khi offline. Bản đã lưu vẫn nguyên.' });
      return;
    }
    const result = await checkFreshness();
    if (result.status === 'uptodate') {
      toast({ title: 'Đã là bản mới nhất', description: `"${report.ReportName}" không có phiên bản mới.` });
    } else if (result.status === 'stale') {
      setUpdateDialogOpen(true);
    } else if (result.status === 'error') {
      toast({ title: 'Không thể kiểm tra', description: `${result.message} Bản đã lưu vẫn nguyên.`, variant: 'destructive' });
    }
  };

  const handleRedownload = async () => {
    // Re-download without deleting first — saveOffline overwrites. If the
    // fetch fails, the existing cached copy is preserved.
    const result = await saveOffline(buildPayload());
    if (result.success) {
      toast({ title: 'Đã tải lại', description: `"${report.ReportName}" đã được cập nhật bản mới nhất.` });
    } else {
      toast({ title: 'Không thể tải lại', description: `${result.message} Bản cũ vẫn còn nguyên.`, variant: 'destructive' });
    }
  };

  if (isOffline) {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCheckForUpdate}
          disabled={isChecking || isSaving}
          className="border-green-500 text-green-700 hover:bg-green-50 text-xs sm:text-sm flex-shrink-0"
          title="Đã lưu offline — bấm để kiểm tra bản mới"
        >
          {isChecking || isSaving ? (
            <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 animate-spin" />
          ) : (
            <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
          )}
          <span className="hidden sm:inline">Đã lưu offline</span>
          <span className="sm:hidden">Offline</span>
        </Button>
        <AlertDialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Có bản mới — tải lại?</AlertDialogTitle>
              <AlertDialogDescription>
                Báo cáo "<strong>{report.ReportName}</strong>" trên máy chủ đã được cập nhật sau lần lưu offline. Bạn có muốn tải bản mới không? Nếu hủy, bản cũ vẫn được giữ nguyên.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Hủy</AlertDialogCancel>
              <AlertDialogAction onClick={handleRedownload}>Tải lại</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleSave}
      disabled={isSaving}
      className="border-gray-400 text-gray-600 hover:bg-gray-100 text-xs sm:text-sm flex-shrink-0"
      title="Lưu để đọc khi không có mạng"
    >
      {isSaving ? (
        <>
          <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 animate-spin" />
          <span className="hidden sm:inline">{saveProgress}%</span>
        </>
      ) : (
        <>
          <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
          <span className="hidden sm:inline">Lưu offline</span>
          <span className="sm:hidden">Lưu</span>
        </>
      )}
    </Button>
  );
};

// ── Shared sub-components ────────────────────────────────────────────────────

interface ReportItem {
  ID: string;
  ReportName: string;
  CreatedOn: string;
  FullURL?: string;
  CreatedByUserID: string;
  is_summary?: boolean;
  [key: string]: unknown;
}

interface UserInfoFn {
  (userId?: string | null, authUserId?: string | null): { name: string; email: string };
}

const SummaryReportCard = ({
  report,
  label,
  getUserInfo,
}: {
  report: ReportItem;
  label: string;
  getUserInfo: UserInfoFn;
}) => {
  const reportId = report.ID || (report as any).id || '';
  const { isOffline: isSavedOffline } = useOfflineReport(reportId);
  const { isOnline } = useNetworkStatus();
  const { toast } = useToast();

  const { opening: openingFile, open: openFileTab } = useOpenInNewTab();
  const handleOpenFile = async () => {
    const fail = (title: string, description: string) =>
      toast({ title, description, variant: 'destructive' });
    if (isOnline) {
      await openFileTab(() => getReportFileUrl(toFileRefs(report)), (m) => fail('Không mở được file', m));
    } else if (isSavedOffline) {
      await openFileTab(async () => {
        const blobUrl = await openOfflineReport(reportId);
        if (!blobUrl) fail('Không tìm thấy file offline', 'Vui lòng kết nối mạng để mở file này.');
        return blobUrl;
      }, (m) => fail('Không mở được file', m));
    } else {
      fail('Không có mạng', 'Báo cáo này chưa được lưu offline. Bấm "Lưu offline" khi có mạng để đọc sau.');
    }
  };

  return (
    <Card className="border-2 border-amber-300 bg-amber-50/50 mb-6">
      <CardHeader className="p-4 sm:p-5">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-amber-500 text-white flex-shrink-0">
            <Crown className="h-4 w-4 sm:h-5 sm:w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="min-w-0">
                <CardTitle className="text-base sm:text-lg text-amber-800">{label}</CardTitle>
                <p className="text-sm font-medium text-amber-900 truncate">{report.ReportName}</p>
                <p className="text-xs text-amber-600 mt-0.5">
                  {new Date(report.CreatedOn).toLocaleDateString('vi-VN')}
                  {' • '}Tạo bởi: {getUserInfo(report.CreatedByUserID, (report as any).auth_user_id).name}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <OfflineSaveButton report={report} />
                {(hasReportFile(toFileRefs(report)) || isSavedOffline) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenFile}
                    disabled={openingFile}
                    className="border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white text-xs sm:text-sm flex-shrink-0"
                  >
                    {openingFile ? <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 animate-spin" /> : <FolderOpen className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />}
                    {openingFile ? 'Đang mở…' : 'Mở file'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
};

const EmptySummaryCard = ({ period, label }: { period: string; label: string }) => (
  <Card className="border border-dashed border-gray-300 bg-gray-50/50 mb-6">
    <CardContent className="flex items-center justify-center py-5">
      <div className="flex items-center gap-2 text-gray-400">
        <Crown className="h-5 w-5" />
        <p className="text-sm">Chưa có {label} cho {period}</p>
      </div>
    </CardContent>
  </Card>
);

const ReportListItem = ({
  report,
  index,
  getUserInfo,
  openFileUrl,
}: {
  report: ReportItem;
  index: number;
  getUserInfo: UserInfoFn;
  openFileUrl: (url: string) => void;
}) => {
  const reportId = report.ID || (report as any).id || '';
  const { isOffline: isSavedOffline, isSaving, isChecking, saveProgress, saveOffline, checkFreshness } = useOfflineReport(reportId);
  const { isOnline } = useNetworkStatus();
  const { toast } = useToast();
  const userInfo = getUserInfo(report.CreatedByUserID, (report as any).auth_user_id);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);

  const buildPayload = () => ({
    id: reportId,
    report_name: report.ReportName,
    period: (report as any).Period,
    created_on: report.CreatedOn,
    full_url: report.FullURL,
    file_url: (report as any).file_url,
    file_id: (report as any).file_id,
    supabase_file_path: (report as any).supabase_file_path,
    summary_by_ai: (report as any).summary_by_ai,
    is_summary: report.is_summary,
    id_group: (report as any).IDGroup,
    created_by_user_id: report.CreatedByUserID,
    auth_user_id: (report as any).auth_user_id,
    updated_at: (report as any).updated_at,
  });

  const handleSave = async () => {
    const result = await saveOffline(buildPayload());
    if (result.success) {
      toast({ title: 'Đã lưu offline', description: `"${report.ReportName}" sẵn sàng đọc khi mất mạng.` });
    } else {
      toast({ title: 'Không thể lưu offline', description: result.message, variant: 'destructive' });
    }
  };

  const handleCheckForUpdate = async () => {
    if (!isOnline) {
      toast({ title: 'Không có mạng', description: 'Không thể kiểm tra bản mới khi offline. Bản đã lưu vẫn nguyên.' });
      return;
    }
    const result = await checkFreshness();
    if (result.status === 'uptodate') {
      toast({ title: 'Đã là bản mới nhất', description: `"${report.ReportName}" không có phiên bản mới.` });
    } else if (result.status === 'stale') {
      setUpdateDialogOpen(true);
    } else if (result.status === 'error') {
      toast({ title: 'Không thể kiểm tra', description: `${result.message} Bản đã lưu vẫn nguyên.`, variant: 'destructive' });
    }
  };

  const handleRedownload = async () => {
    // Re-download overwrites the cached copy. On failure, the existing copy is kept.
    const result = await saveOffline(buildPayload());
    if (result.success) {
      toast({ title: 'Đã tải lại', description: `"${report.ReportName}" đã được cập nhật bản mới nhất.` });
    } else {
      toast({ title: 'Không thể tải lại', description: `${result.message} Bản cũ vẫn còn nguyên.`, variant: 'destructive' });
    }
  };

  const { opening: openingFile, open: openFileTab } = useOpenInNewTab();
  const handleOpenFile = async () => {
    const fail = (title: string, description: string) =>
      toast({ title, description, variant: 'destructive' });
    if (isOnline) {
      await openFileTab(() => getReportFileUrl(toFileRefs(report)), (m) => fail('Không mở được file', m));
    } else if (isSavedOffline) {
      await openFileTab(async () => {
        const blobUrl = await openOfflineReport(reportId);
        if (!blobUrl) fail('Không tìm thấy file offline', 'Vui lòng kết nối mạng để mở file này.');
        return blobUrl;
      }, (m) => fail('Không mở được file', m));
    } else {
      fail('Không có mạng', 'Báo cáo này chưa được lưu offline. Bấm "Lưu offline" khi có mạng để đọc sau.');
    }
  };

  return (
    <Card className="shadow-sm">
      <CardHeader className="p-4 sm:p-5">
        <div className="flex items-center gap-3">
          <div
            className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full text-xs sm:text-sm font-bold flex-shrink-0"
            style={{ backgroundColor: '#05985C', color: 'white' }}
          >
            {index + 1}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="min-w-0">
                <CardTitle
                  className="text-base sm:text-lg break-words leading-tight"
                  style={{ color: '#05985C' }}
                >
                  {report.ReportName}
                </CardTitle>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs sm:text-sm text-gray-500">
                  <span className="truncate">Tạo bởi: {userInfo.name}</span>
                  <span className="truncate hidden sm:inline">Email: {userInfo.email}</span>
                  <span>Ngày: {new Date(report.CreatedOn).toLocaleDateString('vi-VN')}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                {/* Offline save / check-for-update button */}
                {isSavedOffline ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCheckForUpdate}
                      disabled={isChecking || isSaving}
                      className="border-green-500 text-green-700 hover:bg-green-50 text-xs sm:text-sm"
                      title="Đã lưu offline — bấm để kiểm tra bản mới"
                    >
                      {isChecking || isSaving ? (
                        <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 animate-spin" />
                      ) : (
                        <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                      )}
                      <span className="hidden sm:inline">Đã lưu offline</span>
                      <span className="sm:hidden">Offline</span>
                    </Button>
                    <AlertDialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Có bản mới — tải lại?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Báo cáo "<strong>{report.ReportName}</strong>" trên máy chủ đã được cập nhật sau lần lưu offline. Bạn có muốn tải bản mới không? Nếu hủy, bản cũ vẫn được giữ nguyên.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Hủy</AlertDialogCancel>
                          <AlertDialogAction onClick={handleRedownload}>Tải lại</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="border-gray-400 text-gray-600 hover:bg-gray-100 text-xs sm:text-sm"
                    title="Lưu để đọc khi không có mạng"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 animate-spin" />
                        <span className="hidden sm:inline">{saveProgress}%</span>
                      </>
                    ) : (
                      <>
                        <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        <span className="hidden sm:inline">Lưu offline</span>
                        <span className="sm:hidden">Lưu</span>
                      </>
                    )}
                  </Button>
                )}
                {(report as any).ai_version_url && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openFileUrl((report as any).ai_version_url)}
                    className="flex items-center gap-2 border-purple-500 text-purple-600 hover:bg-purple-500 hover:text-white text-xs sm:text-sm flex-shrink-0"
                  >
                    <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
                    AI Version
                  </Button>
                )}
                {hasReportFile(toFileRefs(report)) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenFile()}
                    disabled={openingFile}
                    className="flex items-center gap-2 border-[#05985C] text-[#05985C] hover:bg-[#05985C] hover:text-white text-xs sm:text-sm flex-shrink-0"
                  >
                    {openingFile ? <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" /> : <FolderOpen className="h-3 w-3 sm:h-4 sm:w-4" />}
                    <span className="hidden sm:inline">{openingFile ? 'Đang mở…' : 'Mở file'}</span>
                    <span className="sm:hidden">{openingFile ? '…' : 'Mở'}</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
};

const ReportList = ({
  reports,
  emptyText,
  getUserInfo,
  openFileUrl,
}: {
  reports: ReportItem[];
  emptyText: string;
  getUserInfo: UserInfoFn;
  openFileUrl: (url: string) => void;
}) => {
  if (reports.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500 text-center">{emptyText}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {reports.map((report, index) => (
        <ReportListItem
          key={report.ID || (report as any).id || index}
          report={report}
          index={index}
          getUserInfo={getUserInfo}
          openFileUrl={openFileUrl}
        />
      ))}
    </div>
  );
};

const TabReportContent = ({
  selectedPeriod,
  summaryReports,
  regularReports,
  summaryLabel,
  emptyText,
  getUserInfo,
  openFileUrl,
  recentPeriods,
  noPeriodsText,
}: {
  selectedPeriod: string;
  summaryReports: ReportItem[];
  regularReports: ReportItem[];
  summaryLabel: string;
  emptyText: string;
  getUserInfo: UserInfoFn;
  openFileUrl: (url: string) => void;
  recentPeriods: string[];
  noPeriodsText: string;
}) => {
  if (!selectedPeriod) return null;

  return (
    <div>
      {summaryReports.length > 0 ? (
        summaryReports.map(report => (
          <SummaryReportCard
            key={report.id}
            report={report}
            label={summaryLabel}
            getUserInfo={getUserInfo}
          />
        ))
      ) : (
        <EmptySummaryCard period={selectedPeriod} label={summaryLabel} />
      )}

      <ReportList
        reports={regularReports}
        emptyText={emptyText}
        getUserInfo={getUserInfo}
        openFileUrl={openFileUrl}
      />

      {recentPeriods.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-500 text-center">{noPeriodsText}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────

const ReportSummary: React.FC = () => {
  const { isOnline } = useNetworkStatus();

  // ─── Online data sources (React Query + Supabase) ─────────────────────────
  const {
    data: availablePeriodsOnline = [],
    isLoading: periodsLoading,
    error: periodsError,
  } = useReportPeriods();

  const recentPeriodsOnline = useMemo(
    () => availablePeriodsOnline.sort((a, b) => b.localeCompare(a)).slice(0, 4),
    [availablePeriodsOnline]
  );

  const {
    data: reportsResponse,
    isLoading: reportsLoading,
    error: reportsError,
  } = useReportsByPeriods(recentPeriodsOnline, { limit: 1000 });

  const reportsOnline = useMemo(() => {
    if (!reportsResponse?.data) return [];
    return reportsResponse.data.map(r => ({
      ID: r.legacy_id,
      ReportName: r.report_name,
      CreatedOn: r.created_on,
      FullURL: r.full_url,
      fileID: r.file_id,
      IDGroup: r.id_group,
      Period: r.period,
      CreatedByUserID: r.created_by_user_id,
      is_summary: r.is_summary,
      ...r,
    }));
  }, [reportsResponse?.data]);

  // ─── Offline data source (IndexedDB) ──────────────────────────────────────
  // When the user is offline (or the online query is failing), we fall back
  // to reports they've explicitly saved via the "Lưu offline" button.
  const { offlineReports, isLoading: offlineReportsLoading } = useOfflineReports();

  const reportsFromOffline = useMemo(
    () =>
      offlineReports.map(r => ({
        // Map OfflineReportMeta → the ReportItem shape the page expects.
        ID: r.id,
        id: r.id,
        ReportName: r.report_name,
        CreatedOn: r.created_on || r.saved_at,
        FullURL: r.full_url,
        IDGroup: r.id_group,
        Period: r.period,
        CreatedByUserID: r.created_by_user_id,
        is_summary: r.is_summary,
        file_url: r.file_url,
        supabase_file_path: r.supabase_file_path,
        auth_user_id: r.auth_user_id,
        summary_by_ai: r.summary_by_ai,
      })),
    [offlineReports]
  );

  const availablePeriodsOffline = useMemo(() => {
    const set = new Set<string>();
    for (const r of offlineReports) if (r.period) set.add(r.period);
    return Array.from(set);
  }, [offlineReports]);

  // ─── Merge: prefer online data when present, fall back to offline cache ───
  const useOfflineData = !isOnline && reportsOnline.length === 0;
  const reports = useOfflineData ? reportsFromOffline : reportsOnline;
  const availablePeriods = useOfflineData ? availablePeriodsOffline : availablePeriodsOnline;
  const recentPeriods = useMemo(
    () => [...availablePeriods].sort((a, b) => b.localeCompare(a)).slice(0, 4),
    [availablePeriods]
  );

  const { users, loading: usersLoading } = useUsersFromSupabase();

  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [activeTab, setActiveTab] = useState<'weekly' | 'quality' | '5s' | 'hr' | 'mechanical' | 'bcdgnv'>('weekly');
  const [hasAutoSelected, setHasAutoSelected] = useState(false);

  const getUserInfo: UserInfoFn = useCallback(
    (userId, authUserId) => {
      const user = users.find(u =>
        (userId && u.ID === userId) ||
        (authUserId && u.authUserId === authUserId)
      );
      return {
        name: user?.UserName || 'Unknown',
        email: user?.Email || '',
      };
    },
    [users]
  );

  const { toast } = useToast();
  const openFileUrl = useCallback((url: string) => {
    if (isOnline) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      toast({
        title: 'Không có mạng',
        description: 'Bấm "Lưu offline" trên mỗi báo cáo khi có mạng để đọc khi offline.',
        variant: 'destructive',
      });
    }
  }, [isOnline, toast]);

  const tabFilteredReports = useMemo(() => {
    if (!reports.length) return [];
    if (activeTab === '5s') return reports.filter(r => r.ReportName.toLowerCase().includes('5s'));
    if (activeTab === 'weekly') return reports.filter(r => r.IDGroup === WEEKLY_GROUP_ID && !r.ReportName.toLowerCase().includes('5s'));
    if (activeTab === 'quality') return reports.filter(r => r.IDGroup === QUALITY_GROUP_ID);
    if (activeTab === 'bcdgnv') return reports.filter(r => r.IDGroup === BCDGNV_GROUP_ID);
    return [];
  }, [reports, activeTab]);

  const periodHasData = useCallback(
    (period: string) => tabFilteredReports.some(r => r.Period === period),
    [tabFilteredReports]
  );

  const selectedPeriodReports = useMemo(
    () =>
      tabFilteredReports
        .filter(r => r.Period === selectedPeriod)
        .sort((a, b) => new Date(b.CreatedOn).getTime() - new Date(a.CreatedOn).getTime()),
    [tabFilteredReports, selectedPeriod]
  );

  const summaryReports = useMemo(
    () => selectedPeriodReports.filter(r => r.is_summary === true),
    [selectedPeriodReports]
  );

  const regularReports = useMemo(
    () => selectedPeriodReports.filter(r => !r.is_summary),
    [selectedPeriodReports]
  );

  useEffect(() => {
    if (!recentPeriods.length || !reports.length) return;
    if (hasAutoSelected && periodHasData(selectedPeriod)) return;

    const first = recentPeriods.find(p => periodHasData(p)) ?? recentPeriods[0];
    setSelectedPeriod(first);
    if (!hasAutoSelected) setHasAutoSelected(true);
  }, [recentPeriods, reports, activeTab, periodHasData, selectedPeriod, hasAutoSelected]);

  // Offline: only wait for IndexedDB. Don't block on Supabase queries that
  // will never resolve, and don't show an error for them either.
  const loading = isOnline
    ? (periodsLoading || reportsLoading || usersLoading)
    : offlineReportsLoading;
  if (loading) return <PageLoader type="dashboard" title="Đang tải report summary..." />;

  if (isOnline && (periodsError || reportsError)) {
    return (
      <PageTransition>
        <div className="bg-background p-6">
          <div className="max-w-7xl mx-auto">
            <Alert variant="destructive" className="mt-20">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Lỗi tải dữ liệu:{' '}
                {periodsError?.message || reportsError?.message || 'Unknown error'}
              </AlertDescription>
            </Alert>
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="bg-background p-3 sm:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 sm:mb-8 text-center">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              Report Summary
            </h1>
          </div>

          {/* Period Selection */}
          <div className="mb-6 sm:mb-8 text-center">
            <div className="flex gap-1 sm:gap-2 flex-wrap justify-center">
              {recentPeriods.map(period => {
                const hasData = periodHasData(period);
                return (
                  <Badge
                    key={period}
                    variant={selectedPeriod === period ? 'default' : 'outline'}
                    className={`cursor-pointer transition-all hover:scale-105 text-xs sm:text-sm px-2 py-1 sm:px-3 sm:py-1.5 ${
                      selectedPeriod === period
                        ? 'bg-primary text-primary-foreground'
                        : hasData
                        ? 'hover:bg-gray-100'
                        : 'opacity-50 hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedPeriod(period)}
                    title={hasData ? `${period} - Có dữ liệu` : `${period} - Không có dữ liệu`}
                  >
                    {period}
                    {!hasData && <span className="ml-1 text-[10px]">•</span>}
                  </Badge>
                );
              })}
            </div>
          </div>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onValueChange={v => setActiveTab(v as typeof activeTab)}
            className="mb-6 sm:mb-8"
          >
            <div className="flex justify-center">
              <TabsList className="grid grid-cols-6 max-w-xs sm:max-w-3xl">
                <TabsTrigger value="weekly" className="text-xs sm:text-sm px-2 sm:px-4">
                  <span className="hidden sm:inline">Báo cáo tuần</span>
                  <span className="sm:hidden">Tuần</span>
                </TabsTrigger>
                <TabsTrigger value="quality" className="text-xs sm:text-sm px-2 sm:px-4">
                  Quality
                </TabsTrigger>
                <TabsTrigger value="5s" className="text-xs sm:text-sm px-2 sm:px-4">5S</TabsTrigger>
                <TabsTrigger value="hr" className="text-xs sm:text-sm px-2 sm:px-4">
                  <span className="hidden sm:inline">Nhân sự</span>
                  <span className="sm:hidden">NS</span>
                </TabsTrigger>
                <TabsTrigger value="mechanical" className="text-xs sm:text-sm px-2 sm:px-4">
                  <span className="hidden sm:inline">Cơ điện</span>
                  <span className="sm:hidden">Cơ điện</span>
                </TabsTrigger>
                <TabsTrigger value="bcdgnv" className="text-xs sm:text-sm px-2 sm:px-4">
                  <span className="hidden sm:inline">BC DGNV</span>
                  <span className="sm:hidden">DGNV</span>
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="weekly" className="mt-6">
              <TabReportContent
                selectedPeriod={selectedPeriod}
                summaryReports={summaryReports}
                regularReports={regularReports}
                summaryLabel="Báo cáo tổng hợp tuần"
                emptyText={`Không có báo cáo nào trong tuần ${selectedPeriod}`}
                getUserInfo={getUserInfo}
                openFileUrl={openFileUrl}
                recentPeriods={recentPeriods}
                noPeriodsText="Không tìm thấy period nào có dữ liệu"
              />
            </TabsContent>

            <TabsContent value="quality" className="mt-6">
              <TabReportContent
                selectedPeriod={selectedPeriod}
                summaryReports={summaryReports}
                regularReports={regularReports}
                summaryLabel="Báo cáo tổng hợp chất lượng"
                emptyText={`Không có báo cáo chất lượng nào trong period ${selectedPeriod}`}
                getUserInfo={getUserInfo}
                openFileUrl={openFileUrl}
                recentPeriods={recentPeriods}
                noPeriodsText="Không tìm thấy báo cáo chất lượng nào"
              />
            </TabsContent>

            <TabsContent value="5s" className="mt-6">
              <TabReportContent
                selectedPeriod={selectedPeriod}
                summaryReports={summaryReports}
                regularReports={regularReports}
                summaryLabel="Báo cáo tổng hợp 5S"
                emptyText={`Không có báo cáo 5S nào trong period ${selectedPeriod}`}
                getUserInfo={getUserInfo}
                openFileUrl={openFileUrl}
                recentPeriods={recentPeriods}
                noPeriodsText="Không tìm thấy báo cáo 5S nào"
              />
            </TabsContent>

            <TabsContent value="hr" className="mt-6">
              <HRDashboard />
            </TabsContent>

            <TabsContent value="mechanical" className="mt-6">
              <MechanicalDashboardTab />
            </TabsContent>

            <TabsContent value="bcdgnv" className="mt-6">
              <TabReportContent
                selectedPeriod={selectedPeriod}
                summaryReports={summaryReports}
                regularReports={regularReports}
                summaryLabel="BC Đánh giá Nhân viên tổng hợp"
                emptyText={`Không có BC Đánh giá Nhân viên nào trong period ${selectedPeriod}`}
                getUserInfo={getUserInfo}
                openFileUrl={openFileUrl}
                recentPeriods={recentPeriods}
                noPeriodsText="Không tìm thấy BC Đánh giá Nhân viên nào"
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </PageTransition>
  );
};

export default ReportSummary;
